/**
 * QSR Location-Aware Metrics
 * Computes dashboard-compatible metrics filtered by currentLocation.
 * null = All Locations (aggregate), specific location = that venue only.
 */
import { QSR_DEMO_LOCATIONS, calculateAggregateMetrics } from './qsrConfig';

/**
 * Returns a unified metrics object shaped for the dashboard,
 * filtered to a single location or aggregated across all.
 */
export const getQSRDashboardMetrics = (currentLocation) => {
  if (currentLocation) {
    const loc = QSR_DEMO_LOCATIONS.find(l => l.id === currentLocation.id);
    if (!loc) return buildAllLocationsMetrics();
    return buildSingleLocationMetrics(loc);
  }
  return buildAllLocationsMetrics();
};

function buildAllLocationsMetrics() {
  const agg = calculateAggregateMetrics(QSR_DEMO_LOCATIONS);
  const totalLabourHours = QSR_DEMO_LOCATIONS.reduce((s, l) => s + l.metrics.staff.labourHours, 0);
  const avgLabourCost = QSR_DEMO_LOCATIONS.reduce((s, l) => s + l.metrics.labourCostPercent, 0) / QSR_DEMO_LOCATIONS.length;
  const avgFoodCost = QSR_DEMO_LOCATIONS.reduce((s, l) => s + l.metrics.foodCostPercent, 0) / QSR_DEMO_LOCATIONS.length;
  const totalTransactions = QSR_DEMO_LOCATIONS.reduce((s, l) => s + l.metrics.transactionsPerDay, 0);
  const avgTransaction = QSR_DEMO_LOCATIONS.reduce((s, l) => s + l.metrics.avgTransactionValue, 0) / QSR_DEMO_LOCATIONS.length;

  return {
    revenue: { current: agg.totalMonthlyRevenue, previous: Math.round(agg.totalMonthlyRevenue * 0.92), daily: agg.totalDailyRevenue },
    revenue_change_pct: 8.7,
    customers: { current: totalTransactions * 30, previous: Math.round(totalTransactions * 30 * 0.9) },
    customer_change_pct: 10.3,
    avg_transaction: avgTransaction,
    profitMargin: { current: 100 - avgLabourCost - avgFoodCost },
    labourCost: { current: avgLabourCost },
    netProfit: { current: Math.round(100 - avgLabourCost - avgFoodCost - 18) },
    gross_margin: Math.round(100 - avgFoodCost),
    labour_cost: avgLabourCost,
    net_profit: Math.round(100 - avgLabourCost - avgFoodCost - 18),
    splh: parseFloat(agg.avgSPLH),
    totalStaff: agg.totalStaff,
    totalLabourHours,
    locationCount: agg.locationCount,
    locationName: 'All Locations',
    last_sync: new Date().toISOString(),
  };
}

function buildSingleLocationMetrics(loc) {
  const m = loc.metrics;
  const grossMargin = 100 - m.foodCostPercent;
  const netProfit = grossMargin - m.labourCostPercent - 18;

  return {
    revenue: { current: m.monthlyRevenue, previous: Math.round(m.monthlyRevenue * (1 - loc.performance.changePercent / 100)), daily: m.dailyRevenue },
    revenue_change_pct: loc.performance.changePercent,
    customers: { current: m.transactionsPerDay * 30, previous: Math.round(m.transactionsPerDay * 30 * 0.93) },
    customer_change_pct: loc.performance.changePercent > 0 ? loc.performance.changePercent - 1.5 : loc.performance.changePercent + 1.5,
    avg_transaction: m.avgTransactionValue,
    profitMargin: { current: grossMargin - m.labourCostPercent },
    labourCost: { current: m.labourCostPercent },
    netProfit: { current: Math.round(netProfit) },
    gross_margin: Math.round(grossMargin),
    labour_cost: m.labourCostPercent,
    net_profit: Math.round(netProfit),
    splh: m.splh,
    totalStaff: m.staff.onShift,
    totalLabourHours: m.staff.labourHours,
    locationCount: 1,
    locationName: loc.shortName,
    driveThrough: m.driveThrough,
    last_sync: new Date().toISOString(),
  };
}

/**
 * QSR-specific KPIs for the KPI grid, location-filtered.
 */
export const getQSRKpis = (currentLocation) => {
  const m = getQSRDashboardMetrics(currentLocation);
  return [
    {
      id: 'splh',
      label: 'SPLH',
      value: `$${m.splh.toFixed(2)}`,
      target: '$65.00',
      trend: m.splh >= 65 ? 5 : -3,
      description: 'Sales Per Labour Hour',
    },
    {
      id: 'daily-revenue',
      label: 'Daily Revenue',
      value: `$${(m.revenue.daily || 0).toLocaleString()}`,
      target: currentLocation ? '$10,000' : '$50,000',
      trend: m.revenue_change_pct,
      description: 'Today\'s total revenue',
    },
    {
      id: 'labour-cost',
      label: 'Labour Cost %',
      value: `${m.labourCost.current.toFixed(1)}%`,
      target: '25%',
      trend: m.labourCost.current <= 25 ? 2 : -2,
      description: 'Labour as % of revenue',
    },
    {
      id: 'food-cost',
      label: 'Food Cost %',
      value: `${(100 - m.gross_margin).toFixed(1)}%`,
      target: '31%',
      trend: (100 - m.gross_margin) <= 31 ? 3 : -1,
      description: 'COGS as % of revenue',
    },
  ];
};

/**
 * QSR-specific live metrics, location-filtered.
 */
export const getQSRLiveMetrics = (currentLocation) => {
  const m = getQSRDashboardMetrics(currentLocation);
  const dtLabel = m.driveThrough ? `${m.driveThrough.avgWaitTime} min` : 'N/A';
  return [
    { name: 'Staff On Shift', value: m.totalStaff.toString() },
    { name: 'SPLH Now', value: `$${m.splh.toFixed(0)}` },
    { name: 'DT Wait', value: dtLabel },
    { name: currentLocation ? 'Daily Rev' : 'Network Rev', value: `$${(m.revenue.daily || 0).toLocaleString()}` },
  ];
};

/**
 * QSR-specific wins, location-filtered.
 */
export const getQSRWins = (currentLocation) => {
  if (currentLocation) {
    const loc = QSR_DEMO_LOCATIONS.find(l => l.id === currentLocation.id);
    if (!loc) return [];
    const wins = [];
    if (loc.metrics.splh >= loc.metrics.splhTarget) {
      wins.push({
        title: `${loc.shortName} SPLH Above Target`,
        description: `${loc.shortName} is running at $${loc.metrics.splh.toFixed(2)} SPLH against a $${loc.metrics.splhTarget} target — strong labour efficiency.`,
        metric: `$${loc.metrics.splh.toFixed(2)}`,
        metricLabel: 'current SPLH',
        improvement: `+$${(loc.metrics.splh - loc.metrics.splhTarget).toFixed(2)} above target`,
        source: 'summit_qsr',
      });
    }
    if (loc.performance.trend === 'up') {
      wins.push({
        title: `${loc.shortName} Revenue Trending Up`,
        description: `Revenue at ${loc.shortName} has grown ${loc.performance.changePercent}% vs last period.`,
        metric: `+${loc.performance.changePercent}%`,
        metricLabel: 'revenue growth',
        improvement: `Rank #${loc.performance.rank} of 5`,
        source: 'square',
      });
    }
    if (loc.metrics.driveThrough && loc.metrics.driveThrough.accuracy >= 98) {
      wins.push({
        title: 'Drive-Through Accuracy Excellent',
        description: `${loc.shortName} drive-through is maintaining ${loc.metrics.driveThrough.accuracy}% order accuracy.`,
        metric: `${loc.metrics.driveThrough.accuracy}%`,
        metricLabel: 'accuracy',
        improvement: 'Industry benchmark: 97%',
        source: 'summit_qsr',
      });
    }
    return wins.slice(0, 2);
  }

  // All-locations wins
  return [
    {
      title: 'Southport Leads Network SPLH',
      description: 'Southport is running at $71.20 SPLH — the highest across all 5 locations and 9.5% above target.',
      metric: '$71.20',
      metricLabel: 'SPLH (Southport)',
      improvement: 'Best in network',
      source: 'summit_qsr',
    },
    {
      title: 'Network Revenue Up 8.7%',
      description: 'Total daily network revenue hit $52,300 — an 8.7% increase driven by Southport and Penrith.',
      metric: '+8.7%',
      metricLabel: 'vs last period',
      improvement: '4 of 5 locations growing',
      source: 'square',
    },
  ];
};
